/**
 * Difficulty — three parameter sets, one code path, and one seeded generator.
 *
 * ===========================================================================
 * PARAMETERS, NOT BRANCHES
 * ===========================================================================
 *
 * Rookie, Pro and All-Star run the SAME decision layer and the SAME mechanics.
 * Nothing anywhere reads `params.tier` to choose an algorithm; a tier is a
 * struct of numbers and a set of unlocked mechanics. That is deliberate:
 * difficulty implemented as separate code paths drifts, because a fix to the
 * All-Star path silently leaves Rookie behind, and the tiers stop being
 * comparable. Here a tier can only make the same bot slower to react, less
 * accurate, more frugal with boost, or unable to attempt a skill.
 *
 * The five levers, and what each one costs a bot in play:
 *
 *   reactionTicks       Input lag. The bot decides on the current world and
 *                       then emits that decision `reactionTicks` later, so it
 *                       is always acting on stale information. This is the
 *                       single most human-feeling knob: 0.1 s of lag is 130 uu
 *                       of ball travel on a fast shot.
 *   aimError            Radians of steady bias on every shot direction, drawn
 *                       once per shot from the seeded generator. Not per-tick
 *                       noise: per-tick noise averages out and just looks like
 *                       jitter, while a per-shot bias actually misses.
 *   boostReserve /      Boost economy. A tier that will not spend below its
 *   useBoostForSpeed    reserve arrives later and challenges weaker.
 *   mechanics           Which skills exist at all. A Rookie has no aerial and
 *                       no speed-flip kickoff, so it loses those balls outright
 *                       rather than attempting them badly.
 *   maxCommitDistance   How far the bot will chase. A low value keeps Rookie
 *                       near its own half, which is safer but concedes the
 *                       midfield.
 *
 * ===========================================================================
 * DETERMINISM
 * ===========================================================================
 *
 * There is no `Math.random` anywhere in src/bot. Every stochastic quantity
 * comes from an `Rng` created from an integer seed and threaded explicitly
 * through the bot, so a match is reproducible from (seed, initial state,
 * inputs) alone. `makeRng` is mulberry32 — the same generator the existing
 * physics tests use — chosen because it is 32-bit-integer arithmetic end to
 * end and therefore identical on every platform.
 *
 * ===========================================================================
 * THE "LEARNED (RL POLICY)" TIER
 * ===========================================================================
 *
 * `'learned'` is a SEAM, not a feature. No network is trained here and none is
 * shipped. `difficultyParams('learned')` throws, and `LEARNED_POLICY_AVAILABLE`
 * is false, so a UI that offers the option gets a loud failure rather than a
 * silent downgrade to a scripted bot wearing a different label. The interface a
 * learned policy would implement is `Policy` in ./bot; dropping one in means
 * passing it as `BotConfig.policy` and flipping nothing else.
 */

// ---------------------------------------------------------------------------
// Seeded randomness
// ---------------------------------------------------------------------------

/** A deterministic source of uniform values in [0, 1). */
export interface Rng {
  next(): number;
}

/**
 * mulberry32. Passes gjrand's basic suite, is exactly reproducible across
 * engines because every operation is on a 32-bit integer, and needs one word of
 * state — which is what makes a bot's generator cheap to own per car.
 */
export const makeRng = (seed: number): Rng => {
  let state = seed >>> 0;
  return {
    next(): number {
      state = (state + 0x6d2b79f5) >>> 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },
  };
};

/** Uniform in [lo, hi). */
export const uniform = (rng: Rng, lo: number, hi: number): number => lo + rng.next() * (hi - lo);

/** Uniform in [-magnitude, magnitude]. Zero magnitude does not consume a draw,
 *  so a perfectly accurate tier and an inaccurate one stay in step only if the
 *  caller cares — they do not share a generator, so it does not matter. */
export const symmetric = (rng: Rng, magnitude: number): number =>
  magnitude === 0 ? 0 : uniform(rng, -magnitude, magnitude);

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

export type DifficultyTier = 'rookie' | 'pro' | 'allstar' | 'learned';

/** Tiers that have an actual scripted parameter set behind them. */
export type ScriptedTier = Exclude<DifficultyTier, 'learned'>;

/** Which skills the mechanics layer is allowed to attempt. */
export interface MechanicSet {
  /** Leave the ground to hit a ball above `aerialMinHeight`. */
  aerials: boolean;
  /**
   * Spend the SECOND jump on the way up, timed against the target rather than
   * pressed on a fixed count.
   *
   * Its own flag and not part of `aerials`, because it is a different KIND of
   * skill and the gap it opens is enormous. A held first jump peaks at 223 uu
   * and the second one takes that to 474 uu — the difference between a bot that
   * cannot contest anything above waist height and one that can meet a ball at
   * 600 uu with an empty tank. It is a two-press mechanic on a 1.25 s window
   * whose value falls at 2.43 uu per tick of hesitation, which puts it in the
   * same class as `speedFlip`: something a player learns after they can already
   * aerial, so it unlocks in the same place.
   */
  doubleJump: boolean;
  /** Drive up a wall to meet a ball that will bounce off it. */
  wallReads: boolean;
  /** Flip into the ball on the last stretch of a kickoff. */
  kickoffFlip: boolean;
  /** Diagonal flip cancel on kickoff, worth roughly a car length. */
  speedFlip: boolean;
  /** Chase a demolition when supersonic. */
  demos: boolean;
  /** Handbrake through tight turns and recoveries. */
  powerslide: boolean;
}

export interface DifficultyParams {
  readonly tier: ScriptedTier;
  /** Human-facing name. The UI may show this verbatim. */
  readonly label: string;

  /** Ticks of input lag between deciding and acting. */
  readonly reactionTicks: number;
  /** Extra lag drawn uniformly from [0, this] each time the bot re-decides. */
  readonly reactionJitterTicks: number;

  /** Radians of aim bias per shot, drawn from [-this, this]. */
  readonly aimError: number;
  /** Hard cap on |throttle|, so a weaker tier is genuinely slower. */
  readonly throttleCap: number;

  /** Never spend boost below this. */
  readonly boostReserve: number;
  /** Use boost to close distance, not only for aerials. */
  readonly useBoostForSpeed: boolean;
  /** Below this the bot starts wanting a pad. */
  readonly boostHungerThreshold: number;

  /** Furthest interception the bot will commit to, in uu. */
  readonly maxCommitDistance: number;
  /** Ball height above which an aerial is the only way to reach it. */
  readonly aerialMinHeight: number;

  /** Minimum ticks an intent is held before it may be reconsidered. */
  readonly minIntentTicks: number;
  /** A challenger must beat the incumbent's score by this to take over. */
  readonly hysteresisMargin: number;

  /** How far ahead this tier plans. Lower is not just cheaper, it is worse. */
  readonly predictionHorizonTicks: number;

  readonly mechanics: MechanicSet;
}

const ROOKIE: DifficultyParams = {
  tier: 'rookie',
  label: 'Rookie',
  // 0.1 s of lag. On a 1500 uu/s ball that is 125 uu of error at contact, which
  // is more than the ball's radius — enough to turn a clean shot into a nudge.
  reactionTicks: 12,
  reactionJitterTicks: 6,
  aimError: 0.14,
  throttleCap: 0.8,
  boostReserve: 40,
  useBoostForSpeed: false,
  boostHungerThreshold: 60,
  maxCommitDistance: 3500,
  aerialMinHeight: 400,
  minIntentTicks: 45,
  hysteresisMargin: 0.2,
  predictionHorizonTicks: 240,
  mechanics: {
    aerials: false,
    doubleJump: false,
    wallReads: false,
    kickoffFlip: false,
    speedFlip: false,
    demos: false,
    powerslide: false,
  },
};

const PRO: DifficultyParams = {
  tier: 'pro',
  label: 'Pro',
  reactionTicks: 5,
  reactionJitterTicks: 2,
  aimError: 0.05,
  throttleCap: 1,
  boostReserve: 12,
  useBoostForSpeed: true,
  boostHungerThreshold: 45,
  maxCommitDistance: 6000,
  aerialMinHeight: 300,
  minIntentTicks: 24,
  hysteresisMargin: 0.12,
  predictionHorizonTicks: 480,
  mechanics: {
    aerials: true,
    doubleJump: false,
    wallReads: true,
    kickoffFlip: true,
    speedFlip: false,
    demos: false,
    powerslide: true,
  },
};

const ALLSTAR: DifficultyParams = {
  tier: 'allstar',
  label: 'All-Star',
  reactionTicks: 0,
  reactionJitterTicks: 0,
  aimError: 0,
  throttleCap: 1,
  boostReserve: 0,
  useBoostForSpeed: true,
  boostHungerThreshold: 35,
  maxCommitDistance: 12000,
  aerialMinHeight: 250,
  minIntentTicks: 12,
  hysteresisMargin: 0.08,
  predictionHorizonTicks: 720,
  mechanics: {
    aerials: true,
    doubleJump: true,
    wallReads: true,
    kickoffFlip: true,
    speedFlip: true,
    demos: true,
    powerslide: true,
  },
};

export const DIFFICULTIES: Readonly<Record<ScriptedTier, DifficultyParams>> = {
  rookie: ROOKIE,
  pro: PRO,
  allstar: ALLSTAR,
};

/** Tiers in increasing order of strength. Tests iterate this. */
export const SCRIPTED_TIERS: readonly ScriptedTier[] = ['rookie', 'pro', 'allstar'];

/**
 * No trained policy ships with this project. See the header: the option exists
 * so a learned policy can be dropped in, not because one is available.
 */
export const LEARNED_POLICY_AVAILABLE = false;

export class LearnedPolicyUnavailableError extends Error {
  constructor() {
    super(
      'The "Learned (RL policy)" difficulty has no trained policy in this build. ' +
        'Supply one as BotConfig.policy, or choose Rookie, Pro or All-Star.'
    );
    this.name = 'LearnedPolicyUnavailableError';
  }
}

/**
 * Parameters for a tier. Throws for `'learned'` rather than falling back to a
 * scripted tier — a silent fallback would present a scripted bot as a learned
 * one, which is the one outcome worse than the option not working.
 */
export const difficultyParams = (tier: DifficultyTier): DifficultyParams => {
  if (tier === 'learned') throw new LearnedPolicyUnavailableError();
  return DIFFICULTIES[tier];
};

/** Every selectable tier, for a settings menu. `available` is the honest bit. */
export const DIFFICULTY_OPTIONS: readonly { tier: DifficultyTier; label: string; available: boolean }[] =
  [
    { tier: 'rookie', label: ROOKIE.label, available: true },
    { tier: 'pro', label: PRO.label, available: true },
    { tier: 'allstar', label: ALLSTAR.label, available: true },
    { tier: 'learned', label: 'Learned (RL policy)', available: LEARNED_POLICY_AVAILABLE },
  ];
